/*!
 * @pixi/basis - v6.5.2
 * Compiled Wed, 24 Aug 2022 13:51:19 UTC
 *
 * @pixi/basis is licensed under the MIT License.
 * http://www.opensource.org/licenses/mit-license
 */
import { ExtensionType, BufferResource, Texture, BaseTexture, extensions } from '@pixi/core';
import { TYPES, FORMATS, MIPMAP_MODES, ALPHA_MODES } from '@pixi/constants';
import { INTERNAL_FORMATS, CompressedTextureResource } from '@pixi/compressed-textures';
import { LoaderResource } from '@pixi/loaders';
import { settings } from '@pixi/settings';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

function __generator(thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) { throw t[1]; } return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) { throw new TypeError("Generator is already executing."); }
        while (_) { try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) { return t; }
            if (y = 0, t) { op = [op[0] & 2, t.value]; }
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) { _.ops.pop(); }
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; } }
        if (op[0] & 5) { throw op[1]; } return { value: op[0] ? op[1] : void 0, done: true };
    }
}

var _a, _b, _c;
/**
 * The transcoding formats provided by basis_universal.
 *
 * NOTE: Not all of these formats are supported on WebGL!
 * @ignore
 */
/* eslint-disable camelcase, @typescript-eslint/indent */
var BASIS_FORMATS;
(function (BASIS_FORMATS) {
    BASIS_FORMATS[BASIS_FORMATS["cTFETC1"] = 0] = "cTFETC1";
    BASIS_FORMATS[BASIS_FORMATS["cTFETC2"] = 1] = "cTFETC2";
    BASIS_FORMATS[BASIS_FORMATS["cTFBC1"] = 2] = "cTFBC1";
    BASIS_FORMATS[BASIS_FORMATS["cTFBC3"] = 3] = "cTFBC3";
    BASIS_FORMATS[BASIS_FORMATS["cTFBC4"] = 4] = "cTFBC4";
    BASIS_FORMATS[BASIS_FORMATS["cTFBC5"] = 5] = "cTFBC5";
    BASIS_FORMATS[BASIS_FORMATS["cTFBC7"] = 6] = "cTFBC7";
    BASIS_FORMATS[BASIS_FORMATS["cTFPVRTC1_4_RGB"] = 8] = "cTFPVRTC1_4_RGB";
    BASIS_FORMATS[BASIS_FORMATS["cTFPVRTC1_4_RGBA"] = 9] = "cTFPVRTC1_4_RGBA";
    BASIS_FORMATS[BASIS_FORMATS["cTFASTC_4x4"] = 10] = "cTFASTC_4x4";
    BASIS_FORMATS[BASIS_FORMATS["cTFATC_RGB"] = 11] = "cTFATC_RGB";
    BASIS_FORMATS[BASIS_FORMATS["cTFATC_RGBA_INTERPOLATED_ALPHA"] = 12] = "cTFATC_RGBA_INTERPOLATED_ALPHA";
    BASIS_FORMATS[BASIS_FORMATS["cTFRGBA32"] = 13] = "cTFRGBA32";
    BASIS_FORMATS[BASIS_FORMATS["cTFRGB565"] = 14] = "cTFRGB565";
    BASIS_FORMATS[BASIS_FORMATS["cTFBGR565"] = 15] = "cTFBGR565";
    BASIS_FORMATS[BASIS_FORMATS["cTFRGBA4444"] = 16] = "cTFRGBA4444";
})(BASIS_FORMATS || (BASIS_FORMATS = {}));
/* eslint-enable camelcase, @typescript-eslint/indent */
/**
 * Maps {@link BASIS_FORMATS} to {@link PIXI.INTERNAL_FORMATS}
 * @ignore
 */
var BASIS_FORMAT_TO_INTERNAL_FORMAT = (_a = {},
    _a[BASIS_FORMATS.cTFETC1] = INTERNAL_FORMATS.COMPRESSED_RGB_ETC1_WEBGL,
    _a[BASIS_FORMATS.cTFBC1] = INTERNAL_FORMATS.COMPRESSED_RGB_S3TC_DXT1_EXT,
    _a[BASIS_FORMATS.cTFBC3] = INTERNAL_FORMATS.COMPRESSED_RGBA_S3TC_DXT5_EXT,
    _a[BASIS_FORMATS.cTFPVRTC1_4_RGB] = INTERNAL_FORMATS.COMPRESSED_RGB_PVRTC_4BPPV1_IMG,
    _a[BASIS_FORMATS.cTFPVRTC1_4_RGBA] = INTERNAL_FORMATS.COMPRESSED_RGBA_PVRTC_4BPPV1_IMG,
    _a[BASIS_FORMATS.cTFATC_RGB] = INTERNAL_FORMATS.COMPRESSED_RGB_ATC_WEBGL,
    _a[BASIS_FORMATS.cTFASTC_4x4] = INTERNAL_FORMATS.COMPRESSED_RGBA_ATC_INTERPOLATED_ALPHA_WEBGL,
    _a);
/**
 * Maps {@link BASIS_FORMATS} to {@link PIXI.TYPES}. These formats are a fallback when the basis file cannot be transcoded
 * to a valid compressed texture format.
 *
 * NOTE: {@link BASIS_FORMATS.cTFBGR565} is not supported, while {@link BASIS_FORMATS.cTFRGBA4444} is not implemented by
 *  [at]pixi/basis.
 * @ignore
 */
var BASIS_FORMAT_TO_TYPE = (_b = {},
    _b[BASIS_FORMATS.cTFRGBA32] = TYPES.UNSIGNED_BYTE,
    _b[BASIS_FORMATS.cTFRGB565] = TYPES.UNSIGNED_SHORT_5_6_5,
    _b[BASIS_FORMATS.cTFRGBA4444] = TYPES.UNSIGNED_SHORT_4_4_4_4,
    _b);
/**
 * Maps {@link PIXI.INTERNAL_FORMATS} to {@link BASIS_FORMATS}
 * @ignore
 */
var INTERNAL_FORMAT_TO_BASIS_FORMAT = Object.keys(BASIS_FORMAT_TO_INTERNAL_FORMAT)
    .map(function (key) { return Number(key); })
    .reduce(function (reverseMap, basisFormat) {
    reverseMap[BASIS_FORMAT_TO_INTERNAL_FORMAT[basisFormat]] = basisFormat;
    return reverseMap;
}, {});
/**
 * Enumerates the basis formats with alpha components
 * @ignore
 */
var BASIS_FORMATS_ALPHA = (_c = {},
    _c[BASIS_FORMATS.cTFBC3] = true,
    _c[BASIS_FORMATS.cTFPVRTC1_4_RGBA] = true,
    _c[BASIS_FORMATS.cTFASTC_4x4] = true,
    _c);

/**
 * This wraps the transcoder web-worker functionality; it can be converted into a string to get the source code. It expects
 * you to prepend the transcoder JavaScript code so that the `BASIS` namespace is available.
 *
 * The transcoder worker responds to two types of messages: "init" and "transcode". You must always send the first "init"
 * {@link IInitializeTranscoderMessage} message with the WebAssembly binary; if the transcoder is successfully initialized,
 * the web-worker will respond by sending another {@link ITranscodeResponse} message with `success: true`.
 * @ignore
 */
function TranscoderWorkerWrapper() {
    var basisBinding;
    var messageHandlers = {
        init: function (message) {
            if (!self.BASIS) {
                console.warn('jsSource was not prepended?');
                return {
                    type: 'init',
                    success: false
                };
            }
            self.BASIS({ wasmBinary: message.wasmSource }).then(function (basisLibrary) {
                basisLibrary.initializeBasis();
                basisBinding = basisLibrary;
                self.postMessage({
                    type: 'init',
                    success: true
                });
            });
            return null;
        },
        transcode: function (message) {
            var basisData = message.basisData;
            var BASIS = basisBinding;
            var data = basisData;
            var basisFile = new BASIS.BasisFile(data);
            var imageCount = basisFile.getNumImages();
            var hasAlpha = basisFile.getHasAlpha();
            var basisFormat = hasAlpha
                ? message.rgbaFormat
                : message.rgbFormat;
            var basisFallbackFormat = 14; // BASIS_FORMATS.cTFRGB565 (cannot import values into web-worker!)
            var imageArray = new Array(imageCount);
            var fallbackMode = false;
            if (!basisFile.startTranscoding()) {
                basisFile.close();
                basisFile.delete();
                return {
                    type: 'transcode',
                    requestID: message.requestID,
                    success: false,
                    imageArray: null
                };
            }
            for (var i = 0; i < imageCount; i++) {
                var levels = basisFile.getNumLevels(i);
                var imageResource = {
                    imageID: i,
                    levelArray: new Array(),
                    width: null,
                    height: null
                };
                for (var j = 0; j < levels; j++) {
                    var format = !fallbackMode ? basisFormat : basisFallbackFormat;
                    var width = basisFile.getImageWidth(i, j);
                    var height = basisFile.getImageHeight(i, j);
                    var byteSize = basisFile.getImageTranscodedSizeInBytes(i, j, format);
                    var alignedWidth = (width + 3) & ~3;
                    var alignedHeight = (height + 3) & ~3;
                    // Level 0 is texture's actual width, height
                    if (j === 0) {
                        imageResource.width = alignedWidth;
                        imageResource.height = alignedHeight;
                    }
                    var imageBuffer = new Uint8Array(byteSize);
                    if (!basisFile.transcodeImage(imageBuffer, i, j, format, false, false)) {
                        if (fallbackMode) {
                            // We failed in fallback mode as well!
                            console.error("Basis failed to transcode image " + i + ", level " + j + "!");
                            return { type: 'transcode', requestID: message.requestID, success: false };
                        }
                        /* eslint-disable-next-line max-len */
                        console.warn("Basis failed to transcode image " + i + ", level " + j + "! Retrying to an uncompressed texture format!");
                        i = -1;
                        fallbackMode = true;
                        break;
                    }
                    imageResource.levelArray.push({
                        levelID: j,
                        levelWidth: width,
                        levelHeight: height,
                        levelBuffer: imageBuffer
                    });
                }
                imageArray[i] = imageResource;
            }
            basisFile.close();
            basisFile.delete();
            return {
                type: 'transcode',
                requestID: message.requestID,
                success: true,
                basisFormat: !fallbackMode ? basisFormat : basisFallbackFormat,
                imageArray: imageArray
            };
        }
    };
    self.onmessage = function (e) {
        var msg = e.data;
        var response = messageHandlers[msg.type](msg);
        if (response) {
            self.postMessage(response);
        }
    };
}

/**
 * Worker class for transcoding *.basis files in background threads.
 *
 * To enable asynchronous transcoding, you need to provide the URL to the basis_universal transcoding
 * library.
 * @memberof PIXI.BasisLoader
 */
var TranscoderWorker = /** @class */ (function () {
    function TranscoderWorker() {
        var _this = this;
        this.requests = {};
        /**
         * Handles responses from the web-worker
         * @param e - a message event containing the transcoded response
         */
        this.onMessage = function (e) {
            var data = e.data;
            if (data.type === 'init') {
                if (!data.success) {
                    throw new Error('BasisResource.TranscoderWorker failed to initialize.');
                }
                _this.isInit = true;
                _this.onInit();
            }
            else if (data.type === 'transcode') {
                --_this.load;
                var requestID = data.requestID;
                if (data.success) {
                    _this.requests[requestID].resolve(data);
                }
                else {
                    _this.requests[requestID].reject();
                }
                delete _this.requests[requestID];
            }
        };
        this.isInit = false;
        this.load = 0;
        this.initPromise = new Promise(function (resolve) { _this.onInit = resolve; });
        if (!TranscoderWorker.wasmSource) {
            console.warn('PIXI.resources.BasisResource.TranscoderWorker has not been given the transcoder WASM binary!');
        }
        this.worker = new Worker(TranscoderWorker.workerURL);
        this.worker.onmessage = this.onMessage;
        this.worker.postMessage({
            type: 'init',
            jsSource: TranscoderWorker.jsSource,
            wasmSource: TranscoderWorker.wasmSource
        });
    }
    Object.defineProperty(TranscoderWorker, "workerURL", {
        /** Generated URL for the transcoder worker script. */
        get: function () {
            if (!TranscoderWorker._workerURL) {
                var workerSource = TranscoderWorkerWrapper.toString();
                var beginIndex = workerSource.indexOf('{');
                var endIndex = workerSource.lastIndexOf('}');
                workerSource = workerSource.slice(beginIndex + 1, endIndex);
                if (TranscoderWorker.jsSource) {
                    workerSource = TranscoderWorker.jsSource + "\n" + workerSource;
                }
                TranscoderWorker._workerURL = URL.createObjectURL(new Blob([workerSource]));
            }
            return TranscoderWorker._workerURL;
        },
        enumerable: false,
        configurable: true
    });
    /** @returns a promise that is resolved when the web-worker is initialized */
    TranscoderWorker.prototype.initAsync = function () {
        return this.initPromise;
    };
    /**
     * Creates a promise that will resolve when the transcoding of a *.basis file is complete.
     * @param basisData - *.basis file contents
     * @param rgbaFormat - transcoding format for RGBA files
     * @param rgbFormat - transcoding format for RGB files
     * @returns a promise that is resolved with the transcoding response of the web-worker
     */
    TranscoderWorker.prototype.transcodeAsync = function (basisData, rgbaFormat, rgbFormat) {
        return __awaiter(this, void 0, Promise, function () {
            var requestID, requestPromise;
            var _this = this;
            return __generator(this, function (_a) {
                ++this.load;
                requestID = TranscoderWorker._tempID++;
                requestPromise = new Promise(function (resolve, reject) {
                    _this.requests[requestID] = {
                        resolve: resolve,
                        reject: reject
                    };
                });
                this.worker.postMessage({
                    requestID: requestID,
                    basisData: basisData,
                    rgbaFormat: rgbaFormat,
                    rgbFormat: rgbFormat,
                    type: 'transcode'
                });
                return [2 /*return*/, requestPromise];
            });
        });
    };
    /**
     * Loads the transcoder source code
     * @param jsURL - URL to the javascript basis transcoder
     * @param wasmURL - URL to the wasm basis transcoder
     * @returns A promise that resolves when both the js and wasm transcoders have been loaded.
     */
    TranscoderWorker.loadTranscoder = function (jsURL, wasmURL) {
        var _this = this;
        var jsPromise = fetch(jsURL)
            .then(function (res) { return res.text(); })
            .then(function (text) { TranscoderWorker.jsSource = text; });
        var wasmPromise = fetch(wasmURL)
            .then(function (res) { return res.arrayBuffer(); })
            .then(function (arrayBuffer) { TranscoderWorker.wasmSource = arrayBuffer; });
        return Promise.all([jsPromise, wasmPromise]).then(function (data) {
            _this._onTranscoderInitializedResolve();
            return data;
        });
    };
    /**
     * Set the transcoder source code directly
     * @param jsSource - source for the javascript basis transcoder
     * @param wasmSource - source for the wasm basis transcoder
     */
    TranscoderWorker.setTranscoder = function (jsSource, wasmSource) {
        TranscoderWorker.jsSource = jsSource;
        TranscoderWorker.wasmSource = wasmSource;
    };
    /** a promise that when reslved means the transcoder is ready to be used */
    TranscoderWorker.onTranscoderInitialized = new Promise(function (resolve) {
        TranscoderWorker._onTranscoderInitializedResolve = resolve;
    });
    TranscoderWorker._tempID = 0;
    return TranscoderWorker;
}());

/**
 * Loader plugin for handling BASIS supercompressed texture files.
 *
 * To use this loader, you must bind the basis_universal WebAssembly transcoder. There are two ways of
 * doing this:
 *
 * 1. Adding a &lt;script&gt; tag to your HTML page to the transcoder bundle in this package, and serving
 * the WASM binary from the same location.
 *
 * ```js
 * // Copy ./node_modules/@pixi/basis/assets/basis_.wasm into your assets directory
 * // as well, so it is served from the same folder as the JavaScript!
 * &lt;script src="./node_modules/@pixi/basis/assets/basis_transcoder.js" /&gt;
 * ```
 *
 * NOTE: `basis_transcoder.js` expects the WebAssembly binary to be named `basis_transcoder.wasm`.
 * NOTE-2: This method supports transcoding on the main-thread. Only use this if you have 1 or 2 *.basis
 * files.
 *
 * 2. Loading the transcoder source from a URL.
 *
 * ```js
 * // Use this if you to use the default CDN url for @pixi/basis
 * BasisParser.loadTranscoder();
 *
 * // Use this if you want to serve the transcoder on your own
 * BasisParser.loadTranscoder('./basis_transcoder.js', './basis_transcoder.wasm');
 * ```
 *
 * NOTE: This can only be used with web-workers.
 * @class
 * @memberof PIXI
 * @implements {PIXI.ILoaderPlugin}
 */
var BasisParser = /** @class */ (function () {
    function BasisParser() {
    }
    /**
     * Runs transcoding and populates {@link imageArray}. It will run the transcoding in a web worker
     * if they are available.
     * @private
     */
    BasisParser.transcode = function (arrayBuffer) {
        return __awaiter(this, void 0, Promise, function () {
            var resources;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!(typeof Worker !== 'undefined' && BasisParser.TranscoderWorker.wasmSource)) { return [3 /*break*/, 2]; }
                        return [4 /*yield*/, BasisParser.transcodeAsync(arrayBuffer)];
                    case 1:
                        resources = _a.sent();
                        return [3 /*break*/, 3];
                    case 2:
                        resources = BasisParser.transcodeSync(arrayBuffer);
                        _a.label = 3;
                    case 3: return [2 /*return*/, resources];
                }
            });
        });
    };
    /**
     * Finds a suitable worker for transcoding and sends a transcoding request
     * @private
     * @async
     */
    BasisParser.transcodeAsync = function (arrayBuffer) {
        return __awaiter(this, void 0, Promise, function () {
            var workerPool, leastLoad, worker, i, j, response, basisFormat, imageArray, fallbackMode, imageResources, format, i, j;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!BasisParser.defaultRGBAFormat && !BasisParser.defaultRGBFormat) {
                            BasisParser.autoDetectFormats();
                        }
                        workerPool = BasisParser.workerPool;
                        leastLoad = 0x10000000;
                        worker = null;
                        for (i = 0, j = workerPool.length; i < j; i++) {
                            if (workerPool[i].load < leastLoad) {
                                worker = workerPool[i];
                                leastLoad = worker.load;
                            }
                        }
                        if (!worker) {
                            /* eslint-disable-next-line no-use-before-define */
                            worker = new TranscoderWorker();
                            workerPool.push(worker);
                        }
                        // Wait until worker is ready
                        return [4 /*yield*/, worker.initAsync()];
                    case 1:
                        // Wait until worker is ready
                        _a.sent();
                        return [4 /*yield*/, worker.transcodeAsync(new Uint8Array(arrayBuffer), BasisParser.defaultRGBAFormat.basisFormat, BasisParser.defaultRGBFormat.basisFormat)];
                    case 2:
                        response = _a.sent();
                        basisFormat = response.basisFormat;
                        imageArray = response.imageArray;
                        fallbackMode = basisFormat > 12;
                        if (!fallbackMode) {
                            format = BASIS_FORMAT_TO_INTERNAL_FORMAT[response.basisFormat];
                            // HINT: this.imageArray is CompressedTextureResource[]
                            imageResources = new Array(imageArray.length);
                            for (i = 0, j = imageArray.length; i < j; i++) {
                                imageResources[i] = new CompressedTextureResource(null, {
                                    format: format,
                                    width: imageArray[i].width,
                                    height: imageArray[i].height,
                                    levelBuffers: imageArray[i].levelArray,
                                    levels: imageArray[i].levelArray.length,
                                });
                            }
                        }
                        else {
                            // TODO: BufferResource does not support manual mipmapping.
                            imageResources = imageArray.map(function (image) { return new BufferResource(new Uint16Array(image.levelArray[0].levelBuffer.buffer), {
                                width: image.width,
                                height: image.height,
                            }); });
                        }
                        imageResources.basisFormat = basisFormat;
                        return [2 /*return*/, imageResources];
                }
            });
        });
    };
    /**
     * Runs transcoding on the main thread.
     * @private
     */
    BasisParser.transcodeSync = function (arrayBuffer) {
        if (!BasisParser.defaultRGBAFormat && !BasisParser.defaultRGBFormat) {
            BasisParser.autoDetectFormats();
        }
        var BASIS = BasisParser.basisBinding;
        var data = new Uint8Array(arrayBuffer);
        var basisFile = new BASIS.BasisFile(data);
        var imageCount = basisFile.getNumImages();
        var hasAlpha = basisFile.getHasAlpha();
        var basisFormat = hasAlpha
            ? BasisParser.defaultRGBAFormat.basisFormat
            : BasisParser.defaultRGBFormat.basisFormat;
        var basisFallbackFormat = BASIS_FORMATS.cTFRGB565;
        var imageResources = new Array(imageCount);
        var fallbackMode = BasisParser.fallbackMode;
        if (!basisFile.startTranscoding()) {
            console.error("Basis failed to start transcoding!");
            basisFile.close();
            basisFile.delete();
            return null;
        }
        for (var i = 0; i < imageCount; i++) {
            // Don't transcode all mipmap levels in fallback mode!
            var levels = !fallbackMode ? basisFile.getNumLevels(i) : 1;
            var width = basisFile.getImageWidth(i, 0);
            var height = basisFile.getImageHeight(i, 0);
            var alignedWidth = (width + 3) & ~3;
            var alignedHeight = (height + 3) & ~3;
            var imageLevels = new Array(levels);
            // Transcode mipmap levels into "imageLevels"
            for (var j = 0; j < levels; j++) {
                var levelWidth = basisFile.getImageWidth(i, j);
                var levelHeight = basisFile.getImageHeight(i, j);
                var byteSize = basisFile.getImageTranscodedSizeInBytes(i, 0, !fallbackMode ? basisFormat : basisFallbackFormat);
                imageLevels[j] = {
                    levelID: j,
                    levelBuffer: new Uint8Array(byteSize),
                    levelWidth: levelWidth,
                    levelHeight: levelHeight,
                };
                if (!basisFile.transcodeImage(imageLevels[j].levelBuffer, i, 0, !fallbackMode ? basisFormat : basisFallbackFormat, false, false)) {
                    if (fallbackMode) {
                        console.error("Basis failed to transcode image " + i + ", level " + 0 + "!");
                        break;
                    }
                    else {
                        // Try transcoding to an uncompressed format before giving up!
                        // NOTE: We must start all over again as all Resources must be in compressed OR uncompressed.
                        i = -1;
                        fallbackMode = true;
                        /* eslint-disable-next-line max-len */
                        console.warn("Basis failed to transcode image " + i + ", level " + 0 + " to a compressed texture format. Retrying to an uncompressed fallback format!");
                        continue;
                    }
                }
            }
            var imageResource = void 0;
            if (!fallbackMode) {
                imageResource = new CompressedTextureResource(null, {
                    format: BASIS_FORMAT_TO_INTERNAL_FORMAT[basisFormat],
                    width: alignedWidth,
                    height: alignedHeight,
                    levelBuffers: imageLevels,
                    levels: levels,
                });
            }
            else {
                // TODO: BufferResource doesn't support manual mipmap levels
                imageResource = new BufferResource(new Uint16Array(imageLevels[0].levelBuffer.buffer), { width: width, height: height });
            }
            imageResources[i] = imageResource;
        }
        basisFile.close();
        basisFile.delete();
        var transcodedResources = imageResources;
        transcodedResources.basisFormat = !fallbackMode ? basisFormat : basisFallbackFormat;
        return transcodedResources;
    };
    /**
     * Detects the available compressed texture formats on the device.
     * @param extensions - extensions provided by a WebGL context
     * @ignore
     */
    BasisParser.autoDetectFormats = function (extensions) {
        // Auto-detect WebGL compressed-texture extensions
        if (!extensions) {
            var canvas = settings.ADAPTER.createCanvas();
            var gl = canvas.getContext('webgl');
            if (!gl) {
                console.error('WebGL not available for BASIS transcoding. Silently failing.');
                return;
            }
            extensions = {
                s3tc: gl.getExtension('WEBGL_compressed_texture_s3tc'),
                s3tc_sRGB: gl.getExtension('WEBGL_compressed_texture_s3tc_srgb'),
                etc: gl.getExtension('WEBGL_compressed_texture_etc'),
                etc1: gl.getExtension('WEBGL_compressed_texture_etc1'),
                pvrtc: gl.getExtension('WEBGL_compressed_texture_pvrtc')
                    || gl.getExtension('WEBKIT_WEBGL_compressed_texture_pvrtc'),
                atc: gl.getExtension('WEBGL_compressed_texture_atc'),
                astc: gl.getExtension('WEBGL_compressed_texture_astc'),
            };
        }
        // Discover the available texture formats
        var supportedFormats = {};
        for (var key in extensions) {
            var extension = extensions[key];
            if (!extension) {
                continue;
            }
            Object.assign(supportedFormats, Object.getPrototypeOf(extension));
        }
        // Set the default alpha/non-alpha output formats for basisu transcoding
        for (var i = 0; i < 2; i++) {
            var detectWithAlpha = !!i;
            var internalFormat = void 0;
            var basisFormat = void 0;
            for (var id in supportedFormats) {
                internalFormat = supportedFormats[id];
                basisFormat = INTERNAL_FORMAT_TO_BASIS_FORMAT[internalFormat];
                if (basisFormat !== undefined) {
                    if ((detectWithAlpha && BASIS_FORMATS_ALPHA[basisFormat])
                        || (!detectWithAlpha && !BASIS_FORMATS_ALPHA[basisFormat])) {
                        break;
                    }
                }
            }
            if (internalFormat) {
                BasisParser[detectWithAlpha ? 'defaultRGBAFormat' : 'defaultRGBFormat'] = {
                    textureFormat: internalFormat,
                    basisFormat: basisFormat,
                };
            }
            else {
                BasisParser[detectWithAlpha ? 'defaultRGBAFormat' : 'defaultRGBFormat'] = {
                    textureFormat: TYPES.UNSIGNED_SHORT_5_6_5,
                    basisFormat: BASIS_FORMATS.cTFRGB565,
                };
                BasisParser.fallbackMode = true;
            }
        }
    };
    /**
     * Binds the basis_universal transcoder to decompress *.basis files. You must initialize the transcoder library yourself.
     *
     * ```js
     * import { BasisParser } from '@pixi/basis';
     * import { Loader } from '@pixi/loaders';
     *
     * // window.BASIS() returns a Promise-like object
     * window.BASIS().then((basisLibrary) =>
     * {
     *     // Initialize basis-library; otherwise, transcoded results maybe corrupt!
     *     basisLibrary.initializeBasis();
     *
     *     // Bind BasisParser to the transcoder
     *     BasisParser.bindTranscoder(basisLibrary);
     * });
     * ```
     * @param basisLibrary - the initialized transcoder library
     * @private
     */
    BasisParser.bindTranscoder = function (basisLibrary) {
        BasisParser.basisBinding = basisLibrary;
    };
    /**
     * Loads the transcoder source code for use in {@link PIXI.BasisParser.TranscoderWorker}.
     * @private
     * @param jsURL - URL to the javascript basis transcoder
     * @param wasmURL - URL to the wasm basis transcoder
     */
    BasisParser.loadTranscoder = function (jsURL, wasmURL) {
        return BasisParser.TranscoderWorker.loadTranscoder(jsURL, wasmURL);
    };
    /**
     * Set the transcoder source code directly
     * @private
     * @param jsSource - source for the javascript basis transcoder
     * @param wasmSource - source for the wasm basis transcoder
     */
    BasisParser.setTranscoder = function (jsSource, wasmSource) {
        BasisParser.TranscoderWorker.setTranscoder(jsSource, wasmSource);
    };
    Object.defineProperty(BasisParser, "TRANSCODER_WORKER_POOL_LIMIT", {
        get: function () {
            return this.workerPool.length || 1;
        },
        set: function (limit) {
            // TODO: Destroy workers?
            for (var i = this.workerPool.length; i < limit; i++) {
                this.workerPool[i] = new TranscoderWorker();
                this.workerPool[i].initAsync();
            }
        },
        enumerable: false,
        configurable: true
    });
    /** @ignore */
    BasisParser.extension = ExtensionType.Loader;
    BasisParser.fallbackMode = false;
    BasisParser.workerPool = [];
    BasisParser.TranscoderWorker = TranscoderWorker;
    return BasisParser;
}());

LoaderResource.setExtensionXhrType('basis', LoaderResource.XHR_RESPONSE_TYPE.BUFFER);
/**
 * Loader plugin for handling BASIS supercompressed texture files.
 *
 * To use this loader, you must bind the basis_universal WebAssembly transcoder. There are two ways of
 * doing this:
 *
 * 1. Adding a &lt;script&gt; tag to your HTML page to the transcoder bundle in this package, and serving
 * the WASM binary from the same location.
 *
 * ```js
 * // Copy ./node_modules/@pixi/basis/assets/basis_.wasm into your assets directory
 * // as well, so it is served from the same folder as the JavaScript!
 * &lt;script src="./node_modules/@pixi/basis/assets/basis_transcoder.js" /&gt;
 * ```
 *
 * NOTE: `basis_transcoder.js` expects the WebAssembly binary to be named `basis_transcoder.wasm`.
 * NOTE-2: This method supports transcoding on the main-thread. Only use this if you have 1 or 2 *.basis
 * files.
 *
 * 2. Loading the transcoder source from a URL.
 *
 * ```js
 * // Use this if you to use the default CDN url for @pixi/basis
 * BasisLoader.loadTranscoder();
 *
 * // Use this if you want to serve the transcoder on your own
 * BasisLoader.loadTranscoder('./basis_transcoder.js', './basis_transcoder.wasm');
 * ```
 *
 * NOTE: This can only be used with web-workers.
 * @class
 * @memberof PIXI
 * @implements {PIXI.ILoaderPlugin}
 */
var BasisLoader = /** @class */ (function () {
    function BasisLoader() {
    }
    /**
     * Transcodes the *.basis data when the data is loaded. If the transcoder is not bound yet, it
     * will hook transcoding to {@link BasisResource#onTranscoderInitialized}.
     * @see PIXI.Loader.loaderMiddleware
     * @param resource - loader resource that is checked to see if it is a basis file
     * @param next - callback Function to call when done
     */
    BasisLoader.use = function (resource, next) {
        var _this = this;
        if (resource.extension === 'basis' && resource.data) {
            (function () { return __awaiter(_this, void 0, void 0, function () {
                var resources;
                return __generator(this, function (_a) {
                    switch (_a.label) {
                        case 0:
                            if (!(!BasisParser.basisBinding && (!BasisParser.TranscoderWorker.wasmSource))) { return [3 /*break*/, 2]; }
                            return [4 /*yield*/, TranscoderWorker.onTranscoderInitialized];
                        case 1:
                            _a.sent();
                            _a.label = 2;
                        case 2: return [4 /*yield*/, BasisParser.transcode(resource.data)];
                        case 3:
                            resources = _a.sent();
                            Object.assign(resource, BasisLoader.registerTextures(resource.url, resources, resource.metadata));
                            next();
                            return [2 /*return*/];
                    }
                });
            }); })();
        }
        else {
            next();
        }
    };
    /**
     * Creates textures and adds them to the texture cache
     * @private
     * @param url - url of the texture to be used as its ID for the texture cache
     * @param resources - the transcoded resources
     * @param metadata - resource metadata
     */
    BasisLoader.registerTextures = function (url, resources, metadata) {
        var result = {
            textures: {},
            texture: null,
        };
        if (!resources) {
            return result;
        }
        // Should be a valid TYPES, FORMATS for uncompressed basis formats
        var type = BASIS_FORMAT_TO_TYPE[resources.basisFormat];
        var format = resources.basisFormat !== BASIS_FORMATS.cTFRGBA32 ? FORMATS.RGB : FORMATS.RGBA;
        var resourceList = resources;
        var textures = resourceList.map(function (resource) {
            return (new Texture(new BaseTexture(resource, Object.assign({
                mipmap: resource instanceof CompressedTextureResource && resource.levels > 1
                    ? MIPMAP_MODES.ON_MANUAL
                    : MIPMAP_MODES.OFF,
                alphaMode: ALPHA_MODES.NO_PREMULTIPLIED_ALPHA,
                type: type,
                format: format,
            }, metadata))));
        });
        textures.forEach(function (texture, i) {
            var baseTexture = texture.baseTexture;
            var cacheID = url + "-" + (i + 1);
            BaseTexture.addToCache(baseTexture, cacheID);
            Texture.addToCache(texture, cacheID);
            if (i === 0) {
                BaseTexture.addToCache(baseTexture, url);
                Texture.addToCache(texture, url);
                result.texture = texture;
            }
            result.textures[cacheID] = texture;
        });
        return result;
    };
    /**
     * Binds the basis_universal transcoder to decompress *.basis files. You must initialize the transcoder library yourself.
     *
     * ```js
     * import { BasisLoader } from '@pixi/basis';
     * import { Loader } from '@pixi/loaders';
     *
     * // window.BASIS() returns a Promise-like object
     * window.BASIS().then((basisLibrary) =>
     * {
     *     // Initialize basis-library; otherwise, transcoded results maybe corrupt!
     *     basisLibrary.initializeBasis();
     *
     *     // Bind BasisLoader to the transcoder
     *     BasisLoader.bindTranscoder(basisLibrary);
     * });
     * ```
     * @param basisLibrary - the initialized transcoder library
     * @private
     */
    BasisLoader.bindTranscoder = function (basisLibrary) {
        BasisParser.basisBinding = basisLibrary;
    };
    /**
     * Loads the transcoder source code for use in {@link PIXI.BasisLoader.TranscoderWorker}.
     * @private
     * @param jsURL - URL to the javascript basis transcoder
     * @param wasmURL - URL to the wasm basis transcoder
     */
    BasisLoader.loadTranscoder = function (jsURL, wasmURL) {
        return BasisParser.TranscoderWorker.loadTranscoder(jsURL, wasmURL);
    };
    /**
     * Set the transcoder source code directly
     * @private
     * @param jsSource - source for the javascript basis transcoder
     * @param wasmSource - source for the wasm basis transcoder
     */
    BasisLoader.setTranscoder = function (jsSource, wasmSource) {
        BasisParser.TranscoderWorker.setTranscoder(jsSource, wasmSource);
    };
    /** @ignore */
    BasisLoader.extension = ExtensionType.Loader;
    return BasisLoader;
}());

extensions.add(BasisLoader);

export { BASIS_FORMATS, BASIS_FORMATS_ALPHA, BASIS_FORMAT_TO_INTERNAL_FORMAT, BASIS_FORMAT_TO_TYPE, BasisLoader, BasisParser, INTERNAL_FORMAT_TO_BASIS_FORMAT, TranscoderWorker };
//# sourceMappingURL=basis.mjs.map
